import React, { useState } from "react";
import { Link } from "react-router-dom";
import { WordmarkChip, Eyebrow, Pill, Card, AudienceSwitch } from "../components/shared/BrandPrimitives";
import { IconGantry, IconStack, IconLock } from "../components/shared/Icons";
import { SHOW_MACHINES_PROOF, SHOW_MACHINES_ASSETS } from "../config/flags";

/**
 * Machines home (Part 7 of the master build prompt).
 * Ground Midnight Forest, text Linen, single accent Seafoam.
 * Copy: British English, no contractions, no em/en dashes, banned words absent.
 */
export default function Machines() {
  React.useEffect(() => {
    document.title = "Crowther for Machines | Physical AI on your own terms";
  }, []);

  return (
    <div
      data-testid="machines-root"
      style={{
        background: "var(--c-linen)",
        color: "var(--c-forest)",
        minHeight: "100vh",
        fontFamily: "var(--font-sans)",
      }}
    >
      <MachinesNav />

      {/* S1 · Hero -------------------------------------------------------- */}
      <section
        data-testid="machines-hero"
        style={{
          position: "relative",
          minHeight: "88vh",
          display: "grid",
          gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1.05fr)",
          gap: "var(--s-8)",
          alignItems: "center",
          padding: "var(--s-9) var(--gutter-desktop)",
          maxWidth: "var(--container-max)",
          margin: "0 auto",
        }}
      >
        <div style={{ zIndex: 2 }}>
          <h1
            style={{
              fontFamily: "var(--font-sans)",
              fontSize: "clamp(40px, 5.2vw, 76px)",
              lineHeight: 1.02,
              letterSpacing: "-0.022em",
              fontWeight: 300,
              margin: 0,
              maxWidth: "14ch",
            }}
          >
            We Are Bringing Intelligence to a Billion Machines.
          </h1>
          <p
            style={{
              fontFamily: "var(--font-sans)",
              fontSize: "17.5px",
              lineHeight: 1.55,
              letterSpacing: "-0.005em",
              color: "#3D4A44",
              maxWidth: "40ch",
              marginTop: "var(--s-5)",
            }}
          >
            One platform for building, deploying and operating intelligent machines.
          </p>
          <div style={{ marginTop: "var(--s-6)", display: "flex", alignItems: "center", gap: "var(--s-5)", flexWrap: "wrap" }}>
            <Pill as={Link} to="/plan" variant="primary" data-testid="machines-hero-roadmap">
              Get Your Firm&rsquo;s Free Customised AI Roadmap
            </Pill>
            <Link
              to="/machines/contact"
              data-testid="machines-hero-contact"
              style={{
                fontFamily: "var(--font-sans)",
                fontSize: 14,
                color: "#3D4A44",
                textDecoration: "none",
                borderBottom: "1px solid rgba(22,30,27,0.12)",
                paddingBottom: 2,
              }}
            >
              Talk to Crowther &rarr;
            </Link>
          </div>
        </div>

        <MachinesHeroVideo />

        <div
          aria-hidden
          data-testid="machines-hero-scroll-cue"
          style={{
            position: "absolute",
            left: "50%",
            bottom: 22,
            transform: "translateX(-50%)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 6,
            pointerEvents: "none",
          }}
        >
          <span
            style={{
              width: 8,
              height: 8,
              background: "var(--c-seafoam)",
              animation: "cw-pulse 1.8s ease-in-out infinite",
            }}
          />
          <span
            style={{
              fontFamily: "var(--font-sans)",
              fontSize: 10,
              letterSpacing: "0.22em",
              color: "#3D4A44",
            }}
          >
            SCROLL
          </span>
        </div>
        <style>{`@keyframes cw-pulse { 0%,100% { opacity: 0.35; transform: scale(1); } 50% { opacity: 1; transform: scale(1.4); } }`}</style>
      </section>

      {/* S2 · Statement band (LIGHT) ------------------------------------- */}
      <section
        data-testid="machines-statement"
        style={{
          padding: "var(--statement-py-desktop) var(--gutter-desktop)",
          textAlign: "center",
          background: "var(--c-linen)",
          color: "var(--c-forest)",
        }}
      >
        <p
          style={{
            fontFamily: "var(--font-sans)",
            fontSize: "clamp(28px, 3.2vw, 44px)",
            lineHeight: 1.15,
            letterSpacing: "-0.02em",
            maxWidth: "30ch",
            margin: "0 auto",
            fontWeight: 300,
          }}
        >
          The machines that build, move and grow the physical world are ready for intelligence of their own.
        </p>
      </section>

      {/* S3 · Our Vision — split text and media --------------------------- */}
      <section
        data-testid="machines-vision"
        style={{
          padding: "var(--section-py-desktop) var(--gutter-desktop)",
          maxWidth: "var(--container-max)",
          margin: "0 auto",
          display: "grid",
          gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
          gap: "var(--s-8)",
          alignItems: "center",
        }}
      >
        <div>
          <Eyebrow glyph>OUR VISION</Eyebrow>
          <h2
            style={{
              fontFamily: "var(--font-sans)",
              fontSize: "clamp(32px, 3.6vw, 54px)",
              lineHeight: 1.06,
              letterSpacing: "-0.02em",
              margin: "var(--s-4) 0 var(--s-5)",
              maxWidth: "22ch",
            }}
          >
            Every Machine Will Think. The Question Is for Who.
          </h2>
          <p style={{ color: "#3D4A44", fontSize: "17.5px", lineHeight: 1.6, maxWidth: "56ch", marginTop: "var(--s-4)" }}>
            Haul trucks. Harvesters. Warehouse fleets. Gantry cranes. Assembly cells. Freight on the open road. One by one, the machines of the working world are gaining the ability to perceive, decide and act.
          </p>
          <p style={{ color: "#3D4A44", fontSize: "17.5px", lineHeight: 1.6, maxWidth: "56ch", marginTop: "var(--s-4)" }}>
            Almost all of that change is still ahead of us, and how it happens matters as much as whether it happens. Intelligence can arrive as a rented service, dependent on a distant cloud and governed by someone else&rsquo;s terms. Or it can arrive as a capability the owner holds: running on the machine, learning from the work, answering to the operation.
          </p>
          <p style={{ color: "var(--c-linen)", fontSize: "17.5px", lineHeight: 1.6, maxWidth: "56ch", marginTop: "var(--s-4)" }}>
            Crowther exists to make the second future the normal one. We build machine intelligence the way infrastructure should be built: on site, under your law, with your hands on the keys.
          </p>
          <div style={{ marginTop: "var(--s-6)" }}>
            <Link to="/machines/approach" data-testid="machines-vision-link" style={{ color: "var(--c-emerald)", textDecoration: "none", borderBottom: "1px solid var(--c-emerald)", paddingBottom: 2 }}>
              Our approach &rarr;
            </Link>
          </div>
        </div>
        <div
          data-testid="machines-vision-media"
          style={{
            aspectRatio: "4 / 5",
            borderRadius: "var(--radius-media)",
            border: "1px solid rgba(22,30,27,0.12)",
            background:
              "radial-gradient(120% 80% at 30% 20%, rgba(161,172,156,0.10) 0%, rgba(22,30,27,0) 60%), " +
              "linear-gradient(180deg, #17332B 0%, #0F1A17 100%)",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <svg viewBox="0 0 400 500" width="100%" height="100%" preserveAspectRatio="xMidYMid slice" aria-hidden style={{ position: "absolute", inset: 0 }}>
            <defs>
              <radialGradient id="visionGlow" cx="50%" cy="42%" r="55%">
                <stop offset="0%" stopColor="#A1AC9C" stopOpacity="0.35" />
                <stop offset="100%" stopColor="#A1AC9C" stopOpacity="0" />
              </radialGradient>
              <linearGradient id="ringFade" x1="0" x2="1">
                <stop offset="0%" stopColor="#A1AC9C" stopOpacity="0" />
                <stop offset="50%" stopColor="#A1AC9C" stopOpacity="0.9" />
                <stop offset="100%" stopColor="#A1AC9C" stopOpacity="0" />
              </linearGradient>
            </defs>
            <circle cx="200" cy="210" r="180" fill="url(#visionGlow)" />
            {[60, 100, 140, 180].map((r) => (
              <circle key={r} cx="200" cy="210" r={r} fill="none" stroke="url(#ringFade)" strokeWidth="1" strokeDasharray="2 4" opacity="0.5" />
            ))}
            {[
              [88, 128], [312, 148], [156, 96], [244, 262], [140, 302], [280, 320], [200, 210], [96, 220], [304, 232],
            ].map(([x, y], i) => (
              <g key={i}>
                <circle cx={x} cy={y} r={i === 6 ? 5 : 2.4} fill="#A1AC9C" opacity={i === 6 ? 1 : 0.7} />
                {i === 6 && <circle cx={x} cy={y} r="12" fill="none" stroke="#A1AC9C" strokeWidth="1" opacity="0.5" />}
              </g>
            ))}
            {[[88, 128, 200, 210], [312, 148, 200, 210], [244, 262, 200, 210], [140, 302, 200, 210], [280, 320, 200, 210]].map((c, i) => (
              <line key={i} x1={c[0]} y1={c[1]} x2={c[2]} y2={c[3]} stroke="#A1AC9C" strokeWidth="0.6" strokeDasharray="1 3" opacity="0.35" />
            ))}
            <g fontFamily="'Berkeley Mono','JetBrains Mono', ui-monospace, monospace" fontSize="9" fill="#A1AC9C" opacity="0.7">
              <text x="24" y="30">SITE.01</text>
              <text x="330" y="30">LAT 6.5°N</text>
              <text x="24" y="486">FLEET · 34</text>
              <text x="300" y="486">UPLINK · OK</text>
            </g>
          </svg>
          <div
            style={{
              position: "absolute", left: 24, bottom: 24, right: 24,
              fontFamily: "var(--font-sans)", fontSize: 12, letterSpacing: "0.22em",
              color: "var(--c-seafoam)", textTransform: "uppercase", opacity: 0.75,
            }}
          >
            {"\u25A0"} On site &nbsp;&middot;&nbsp; On your terms
          </div>
        </div>
      </section>

      {/* S4 · The Platform — four alternating rows (LIGHT) ---------------- */}
      <section
        id="platform"
        data-testid="machines-platform"
        style={{
          background: "var(--c-linen)",
          color: "var(--c-forest)",
        }}
      >
        <div style={{ padding: "var(--section-py-desktop) var(--gutter-desktop)", maxWidth: "var(--container-max)", margin: "0 auto" }}>
        <Eyebrow glyph>THE PLATFORM</Eyebrow>
        <h2
          style={{
            fontFamily: "var(--font-sans)",
            fontSize: "clamp(32px, 3.6vw, 54px)",
            lineHeight: 1.06,
            letterSpacing: "-0.02em",
            marginTop: "var(--s-4)",
            marginBottom: "var(--s-4)",
            maxWidth: "22ch",
            fontWeight: 300,
            color: "var(--c-forest)",
          }}
        >
          One Platform. Every Machine Class.
        </h2>
        <p style={{ color: "#3D4A44", fontSize: "17.5px", lineHeight: 1.6, maxWidth: "68ch", marginBottom: "var(--s-8)" }}>
          Four products, built to work together or apart. Together they take a machine from manual operation to supervised intelligence to full autonomy, and they do it without surrendering the machine, the data or the site to anyone else.
        </p>

        {[
          { name: "Machine Autonomy", kicker: "The Machine Learns Its Job.", body: "An end to end autonomy stack for machines that move, engineered for the real conditions of working sites: dust, weather, gradient, degraded networks and mixed fleets of old and new equipment. Assistance today, supervised autonomy next, full autonomy when the evidence says so. Every step up is a decision the owner makes, not a switch a vendor flips." },
          { name: "Machine OS", kicker: "One Operating Layer for Every Machine You Own.", body: "The software life of a fleet in one place: onboard runtime, over the air updates on your schedule, telemetry, integrations and coordination between machines. Built for mixed fleets across manufacturers and generations, so a ten year old hauler and a new loader follow the same rules and report to the same screen." },
          { name: "Machine Data Engine", kicker: "Every Hour of Work Makes the Fleet Smarter.", body: "Operating data is the most valuable thing a working fleet produces, and most of it is currently thrown away or given away. The Data Engine collects, curates and labels what your machines experience, feeds simulation and validation, and returns better models to the fleet. The loop runs on infrastructure you control, so the asset it builds belongs to the site that built it." },
          { name: "Operator Intelligence", kicker: "People Stay in Command.", body: "The human half of the platform: on machine interfaces, remote supervision and control room views that let a small team direct many machines with confidence. Autonomy should promote operators, not replace them. The person responsible for a site can always see, question and override what a machine intends to do." },
        ].map((p, i) => {
          const reverse = i % 2 === 1;
          return (
            <Link
              key={p.name}
              to="/machines/products"
              data-testid={`machines-platform-row-${i + 1}`}
              style={{ display: "block", textDecoration: "none", color: "inherit" }}
            >
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
                  gap: "var(--s-8)",
                  alignItems: "center",
                  paddingTop: "var(--s-9)",
                  paddingBottom: "var(--s-9)",
                  borderTop: "1px solid rgba(22,30,27,0.12)",
                }}
              >
                <div style={{ order: reverse ? 2 : 1 }}>
                  <div style={{ fontFamily: "var(--font-sans)", fontSize: 12, letterSpacing: "0.16em", textTransform: "uppercase", color: "var(--c-emerald)", marginBottom: "var(--s-3)" }}>
                    {`0${i + 1}`} &nbsp;&middot;&nbsp; {p.name}
                  </div>
                  <h3 style={{ fontFamily: "var(--font-sans)", fontSize: "clamp(26px, 2.6vw, 38px)", lineHeight: 1.1, letterSpacing: "-0.018em", margin: 0, maxWidth: "22ch", color: "var(--c-forest)", fontWeight: 300 }}>
                    {p.kicker}
                  </h3>
                  <p style={{ color: "#3D4A44", fontSize: "17px", lineHeight: 1.6, marginTop: "var(--s-4)", maxWidth: "56ch" }}>
                    {p.body}
                  </p>
                  <div style={{ marginTop: "var(--s-5)", color: "var(--c-emerald)", fontSize: 14 }}>
                    Read the product &rarr;
                  </div>
                </div>
                <div
                  aria-hidden
                  style={{
                    order: reverse ? 1 : 2,
                    aspectRatio: "16 / 10",
                    borderRadius: "var(--radius-media)",
                    border: "1px solid rgba(22,30,27,0.12)",
                    background:
                      "radial-gradient(80% 60% at 70% 30%, rgba(161,172,156,0.12) 0%, rgba(22,30,27,0) 60%), " +
                      "linear-gradient(180deg, #16302A 0%, #0E1815 100%)",
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  <ProductDiagram kind={p.name} />
                  <div style={{ position: "absolute", left: 20, bottom: 18, fontFamily: "var(--font-sans)", fontSize: 11, letterSpacing: "0.22em", textTransform: "uppercase", color: "var(--c-seafoam)", opacity: 0.85 }}>
                    {"\u25A0"} {p.name.toUpperCase()}
                  </div>
                </div>
              </div>
            </Link>
          );
        })}

        <div style={{ marginTop: "var(--s-8)" }}>
          <Link to="/machines/products" data-testid="machines-platform-link" style={{ color: "var(--c-emerald)", textDecoration: "none", borderBottom: "1px solid var(--c-emerald)", paddingBottom: 2 }}>
            Explore the platform &rarr;
          </Link>
        </div>
        </div>
      </section>

      {/* S5 · How It Begins — four numbered steps ------------------------- */}
      <section
        data-testid="machines-steps"
        style={{
          padding: "var(--section-py-desktop) var(--gutter-desktop)",
          maxWidth: "var(--container-max)",
          margin: "0 auto",
          borderTop: "1px solid rgba(22,30,27,0.12)",
        }}
      >
        <Eyebrow glyph>HOW IT BEGINS</Eyebrow>
        <h2 style={{ fontFamily: "var(--font-sans)", fontSize: "clamp(32px, 3.6vw, 54px)", lineHeight: 1.06, letterSpacing: "-0.02em", marginTop: "var(--s-4)", marginBottom: "var(--s-4)", maxWidth: "22ch" }}>
          Autonomy Is Earned in Stages.
        </h2>
        <p style={{ color: "#3D4A44", fontSize: "17.5px", lineHeight: 1.6, maxWidth: "60ch", marginBottom: "var(--s-8)" }}>
          No fleet becomes intelligent overnight, and no honest supplier promises that it will. This is the path we walk with every operation.
        </p>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "var(--s-5)" }} data-testid="machines-steps-grid">
          {[
            { n: "01", t: "Understand the Fleet", b: "We start with your machines, your site and your economics: which tasks repeat, which risks matter, where the hours and the money go. The output is a concrete plan, not a pitch." },
            { n: "02", t: "Integrate and Retrofit", b: "Intelligence is fitted to the equipment you already own wherever possible. Sensing, compute and software arrive as an upgrade to your capital, not a demand to replace it." },
            { n: "03", t: "Supervise", b: "Machines begin working under human supervision, from the seat or the control room. Every hour builds evidence, and every exception becomes a lesson the whole fleet learns." },
            { n: "04", t: "Expand on Evidence", b: "Autonomy widens task by task, area by area, as the record justifies it. You see the same evidence we do, and the pace is yours to set." },
          ].map((s) => (
            <div key={s.n} data-testid={`machines-step-${s.n}`}>
              <div style={{ fontFamily: "var(--font-sans)", fontSize: "56px", lineHeight: 1, color: "var(--c-emerald)", letterSpacing: "-0.02em" }}>
                {s.n}
              </div>
              <h3 style={{ fontFamily: "var(--font-sans)", fontSize: "22px", lineHeight: 1.2, letterSpacing: "-0.015em", margin: "var(--s-4) 0 var(--s-3)", fontWeight: 300 }}>
                {s.t}
              </h3>
              <p style={{ color: "#3D4A44", fontSize: 15, lineHeight: 1.6 }}>
                {s.b}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* S6 · FIG.1 The Sovereign Machine Stack — terminal ---------------- */}
      <section
        data-testid="machines-fig-stack"
        style={{
          padding: "var(--section-py-desktop) var(--gutter-desktop)",
          maxWidth: "var(--container-max)",
          margin: "0 auto",
        }}
      >
        <div
          style={{
            fontFamily: "var(--font-sans)",
            fontSize: 11,
            letterSpacing: "0.22em",
            color: "#3D4A44",
            textTransform: "uppercase",
            marginBottom: "var(--s-4)",
          }}
        >
          FIG.1 &nbsp;&middot;&nbsp; The Sovereign Machine Stack
        </div>

        <div
          data-testid="machines-fig-panel"
          style={{
            borderRadius: 12,
            border: "1px solid rgba(161,172,156,0.22)",
            background: "#07100E",
            boxShadow: "0 30px 80px rgba(0,0,0,0.55), inset 0 0 0 1px rgba(255,255,255,0.02)",
            overflow: "hidden",
          }}
        >
          {/* Chrome bar */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "10px 14px",
              background: "linear-gradient(180deg, #0F1B18 0%, #0B1512 100%)",
              borderBottom: "1px solid rgba(161,172,156,0.15)",
              fontFamily: "'Berkeley Mono','JetBrains Mono','IBM Plex Mono', ui-monospace, monospace",
              fontSize: 11,
              color: "rgba(246,241,234,0.55)",
              letterSpacing: "0.04em",
            }}
          >
            <span aria-hidden style={{ width: 10, height: 10, borderRadius: "50%", background: "#FF5F57", boxShadow: "0 0 0 1px rgba(0,0,0,0.3)" }} />
            <span aria-hidden style={{ width: 10, height: 10, borderRadius: "50%", background: "#FEBC2E", boxShadow: "0 0 0 1px rgba(0,0,0,0.3)" }} />
            <span aria-hidden style={{ width: 10, height: 10, borderRadius: "50%", background: "#28C840", boxShadow: "0 0 0 1px rgba(0,0,0,0.3)" }} />
            <span style={{ marginLeft: 12 }}>crowther@site</span>
            <span style={{ opacity: 0.4 }}>:</span>
            <span style={{ color: "#A1AC9C", opacity: 0.85 }}>~/stack</span>
            <span style={{ opacity: 0.4 }}>&nbsp;&mdash;&nbsp;fig-1.tsx</span>
            <span style={{ marginLeft: "auto", opacity: 0.4 }}>bash</span>
          </div>

          {/* Terminal body */}
          <div
            style={{
              padding: "22px 22px 26px",
              fontFamily: "'Berkeley Mono','JetBrains Mono','IBM Plex Mono', ui-monospace, monospace",
              fontSize: "clamp(11px, 1vw, 13.5px)",
              lineHeight: 1.65,
              color: "#D9EDE6",
              background:
                "radial-gradient(120% 100% at 50% 0%, rgba(161,172,156,0.06) 0%, rgba(7,16,14,0) 60%), " +
                "repeating-linear-gradient(0deg, rgba(161,172,156,0.03) 0 1px, transparent 1px 3px)",
              overflowX: "auto",
            }}
          >
            <div style={{ opacity: 0.55, marginBottom: 6 }}>
              <span style={{ color: "#A1AC9C" }}>$</span> cat sovereign-machine.stack
            </div>
            {[
              { name: "OPERATOR INTELLIGENCE", meta: "supervision \u00B7 override \u00B7 control room" },
              { name: "MACHINE AUTONOMY", meta: "perceive \u00B7 plan \u00B7 act, on the machine" },
              { name: "MACHINE OS", meta: "runtime \u00B7 updates \u00B7 fleet coordination" },
              { name: "MACHINE DATA ENGINE", meta: "collect \u00B7 simulate \u00B7 validate \u00B7 improve" },
            ].map((layer, i, arr) => (
              <React.Fragment key={layer.name}>
                <div style={{ color: "rgba(217,237,230,0.45)" }}>
                  {(i === 0 ? "\u250C" : "\u251C") + "\u2500".repeat(52) + (i === 0 ? "\u2510" : "\u2524")}
                </div>
                <div>
                  <span style={{ color: "rgba(217,237,230,0.45)" }}>{"\u2502  "}</span>
                  <span style={{ color: "#A1AC9C", fontWeight: 300, letterSpacing: "0.06em" }}>{layer.name}</span>
                </div>
                <div>
                  <span style={{ color: "rgba(217,237,230,0.45)" }}>{"\u2502  "}</span>
                  <span style={{ color: "rgba(217,237,230,0.72)" }}>{layer.meta}</span>
                </div>
                {i === arr.length - 1 && (
                  <>
                    <div style={{ color: "rgba(217,237,230,0.45)" }}>
                      {"\u251C" + "\u2500".repeat(52) + "\u2524"}
                    </div>
                    <div>
                      <span style={{ color: "rgba(217,237,230,0.45)" }}>{"\u2502  "}</span>
                      <span style={{ color: "#FFBC47", fontWeight: 300, letterSpacing: "0.06em" }}>
                        YOUR SITE {"\u00B7"} YOUR INFRASTRUCTURE {"\u00B7"} YOUR KEYS
                      </span>
                    </div>
                    <div>
                      <span style={{ color: "rgba(217,237,230,0.45)" }}>{"\u2502  "}</span>
                      <span style={{ color: "rgba(217,237,230,0.72)" }}>
                        access granted by the owner, revoked by the owner
                      </span>
                    </div>
                    <div style={{ color: "rgba(217,237,230,0.45)" }}>
                      {"\u2514" + "\u2500".repeat(52) + "\u2518"}
                    </div>
                  </>
                )}
              </React.Fragment>
            ))}

            <div style={{ marginTop: 14, opacity: 0.75 }}>
              <span style={{ color: "#A1AC9C" }}>$</span> stack.verify
            </div>
            <div style={{ opacity: 0.7 }}>
              <span style={{ color: "#28C840" }}>ok</span> <span style={{ color: "rgba(217,237,230,0.55)" }}>every layer runs where the work happens.</span>
            </div>
            <div style={{ opacity: 0.7 }}>
              <span style={{ color: "#28C840" }}>ok</span> <span style={{ color: "rgba(217,237,230,0.55)" }}>bottom line is the permission model, not a slogan.</span>
            </div>
            <div style={{ marginTop: 6 }}>
              <span style={{ color: "#A1AC9C" }}>$</span>{" "}
              <span aria-hidden style={{ display: "inline-block", width: 8, height: 14, background: "#A1AC9C", verticalAlign: "-2px", animation: "cw-caret 1s steps(2) infinite" }} />
            </div>
          </div>
        </div>
        <style>{`@keyframes cw-caret { 50% { opacity: 0; } }`}</style>
        <p style={{ color: "#3D4A44", fontSize: 14, marginTop: "var(--s-5)", maxWidth: "68ch" }}>
          Every layer runs where the work happens. The bottom line is not a slogan; it is the permission model.
        </p>
      </section>

      {/* S7 · Industries — six cards (LIGHT) ------------------------------ */}
      <section
        data-testid="machines-industries"
        style={{
          background: "var(--c-linen)",
          color: "var(--c-forest)",
        }}
      >
        <div style={{ padding: "var(--section-py-desktop) var(--gutter-desktop)", maxWidth: "var(--container-max)", margin: "0 auto" }}>
        <Eyebrow glyph>INDUSTRIES</Eyebrow>
        <h2 style={{ fontFamily: "var(--font-sans)", fontSize: "clamp(32px, 3.6vw, 54px)", lineHeight: 1.06, letterSpacing: "-0.02em", marginTop: "var(--s-4)", marginBottom: "var(--s-8)", maxWidth: "22ch", fontWeight: 300, color: "var(--c-forest)" }}>
          Built for the Work the World Depends On.
        </h2>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: "var(--s-5)",
          }}
        >
          {[
            { slug: "manufacturing", name: "Manufacturing", line: "The factory floor is learning to run itself." },
            { slug: "logistics", name: "Logistics & Warehousing", line: "Every shelf, tote and trailer, accounted for." },
            { slug: "agriculture", name: "Agriculture", line: "Intelligence that grows with the season." },
            { slug: "mining", name: "Mining & Quarrying", line: "The hardest ground demands the steadiest machines." },
            { slug: "freight", name: "Freight & Haulage", line: "The goods must move, more safely and at lower cost per kilometre." },
            { slug: "ports", name: "Ports & Terminals", line: "Where everything the world trades changes hands." },
          ].map((ind) => (
            <Link
              key={ind.slug}
              to={`/machines/industries/${ind.slug}`}
              data-testid={`machines-industry-tile-${ind.slug}`}
              style={{ textDecoration: "none", color: "inherit" }}
            >
              <div style={{ background: "#ECE6DB", border: "1px solid rgba(22,30,27,0.10)", borderRadius: "var(--radius-card)", padding: "var(--s-6)", height: "100%" }}>
                <div style={{ fontFamily: "var(--font-sans)", fontSize: 12, letterSpacing: "0.16em", textTransform: "uppercase", color: "var(--c-emerald)", marginBottom: "var(--s-3)" }}>
                  {ind.name}
                </div>
                <p style={{ fontFamily: "var(--font-sans)", fontSize: 22, lineHeight: 1.2, letterSpacing: "-0.015em", margin: 0, color: "var(--c-forest)", fontWeight: 300 }}>
                  {ind.line}
                </p>
                <div style={{ marginTop: "var(--s-4)", color: "var(--c-emerald)", fontSize: 14 }}>
                  Read the sector view &rarr;
                </div>
              </div>
            </Link>
          ))}
        </div>
        </div>
      </section>

      {/* S8 · Approach (exactly three cards) ----------------------------- */}
      <section
        id="approach"
        data-testid="machines-approach"
        style={{ padding: "var(--section-py-desktop) var(--gutter-desktop)", maxWidth: "var(--container-max)", margin: "0 auto" }}
      >
        <Eyebrow glyph>APPROACH</Eyebrow>
        <h2
          style={{
            fontFamily: "var(--font-sans)",
            fontSize: "clamp(32px, 3.6vw, 54px)",
            lineHeight: 1.06,
            letterSpacing: "-0.02em",
            marginTop: "var(--s-4)",
            marginBottom: "var(--s-8)",
            maxWidth: "20ch",
          }}
        >
          Built for Machines That Cannot Fail Quietly.
        </h2>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "var(--s-5)" }}>
          {[
            {
              icon: <IconGantry size={28} />,
              title: "Built Where the Machine Works",
              body:
                "Intelligence runs on the machine and on infrastructure at the site. It keeps working when the network does not, and it answers to the people who own it.",
            },
            {
              icon: <IconStack size={28} />,
              title: "One Stack Across Classes",
              body:
                "One platform serves every machine class. What the system learns hauling in a quarry sharpens what it does stacking a warehouse, and every site benefits.",
            },
            {
              icon: <IconLock size={28} />,
              title: "Deny by Default, Even On Board",
              body:
                "Access to the machine, its data and its controls is granted by the owner and revoked by the owner. That rule holds on the machine itself.",
            },
          ].map((c) => (
            <Card key={c.title} tone="light" padded>
              <div style={{ color: "var(--c-emerald)", marginBottom: "var(--s-4)" }}>{c.icon}</div>
              <h3 style={{ fontFamily: "var(--font-sans)", fontSize: "22px", lineHeight: 1.2, letterSpacing: "-0.015em", margin: 0, fontWeight: 300 }}>
                {c.title}
              </h3>
              <p style={{ color: "#3D4A44", marginTop: "var(--s-3)", fontSize: 15, lineHeight: 1.6 }}>
                {c.body}
              </p>
            </Card>
          ))}
        </div>
        <div style={{ marginTop: "var(--s-7)" }}>
          <Link to="/machines/approach" data-testid="machines-approach-link" style={{ color: "var(--c-emerald)", textDecoration: "none", borderBottom: "1px solid var(--c-emerald)", paddingBottom: 2 }}>
            How we build &rarr;
          </Link>
        </div>
      </section>

      {/* S9 · Research (Emerald-tinted light band) ----------------------- */}
      <section
        id="research"
        data-testid="machines-research"
        style={{
          padding: "var(--section-py-desktop) var(--gutter-desktop)",
          background: "linear-gradient(180deg, #E7E1D5 0%, var(--c-linen) 100%)",
          borderTop: "1px solid rgba(22,30,27,0.12)",
          borderBottom: "1px solid rgba(22,30,27,0.12)",
        }}
      >
        <div style={{ maxWidth: "var(--container-max)", margin: "0 auto" }}>
          <Eyebrow glyph>RESEARCH</Eyebrow>
          <h2 style={{ fontFamily: "var(--font-sans)", fontSize: "clamp(32px, 3.6vw, 54px)", lineHeight: 1.06, letterSpacing: "-0.02em", marginTop: "var(--s-4)", maxWidth: "24ch" }}>
            The Same Discipline, Pointed at Machines.
          </h2>
          <p style={{ color: "#3D4A44", maxWidth: "68ch", marginTop: "var(--s-4)", fontSize: "17.5px", lineHeight: 1.6 }}>
            Crowther runs one research programme across both sides of the company. The habits that make our enterprise systems dependable, careful evaluation, reproducible results and honest reporting, are the same habits we bring to machine intelligence. We publish what we can defend and we deploy what we have proven, in that order.
          </p>
          <div style={{ marginTop: "var(--s-6)" }}>
            <Link to="/enterprise/research" data-testid="machines-research-link" style={{ color: "var(--c-emerald)", textDecoration: "none", borderBottom: "1px solid var(--c-emerald)", paddingBottom: 2 }}>
              Read our research &rarr;
            </Link>
          </div>
        </div>
      </section>

      {/* S10 · One Company, Two Sides — inversion split ------------------- */}
      <section
        data-testid="machines-two-sides"
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
        }}
      >
        <div
          style={{
            background: "var(--c-linen)",
            color: "var(--c-forest)",
            padding: "var(--section-py-desktop) var(--gutter-desktop)",
          }}
        >
          <div style={{ fontFamily: "var(--font-sans)", fontSize: 12, letterSpacing: "0.16em", textTransform: "uppercase", color: "var(--c-emerald)" }}>
            <span aria-hidden style={{ display: "inline-block", width: 8, height: 8, background: "var(--c-emerald)", marginRight: 8 }} />
            ONE COMPANY, TWO SIDES
          </div>
          <h2 style={{ fontFamily: "var(--font-sans)", fontSize: "clamp(30px, 3.2vw, 46px)", lineHeight: 1.1, letterSpacing: "-0.02em", margin: "var(--s-4) 0 var(--s-4)", maxWidth: "22ch" }}>
            The Intelligence That Runs Your Machines Can Run Your Business.
          </h2>
          <p style={{ fontSize: "17.5px", lineHeight: 1.6, maxWidth: "56ch", color: "#2A342F" }}>
            Crowther builds sovereign AI for people and enterprises as well as machines. Banks, insurers, telecommunications operators and regulators run our systems inside their own walls, on their own infrastructure, under their own keys. The rule is the same on the trading floor and the factory floor: your AI, your control.
          </p>
          <div style={{ marginTop: "var(--s-6)", display: "flex", flexWrap: "wrap", gap: "var(--s-4)", alignItems: "center" }}>
            <Pill as={Link} to="/plan" variant="primary" data-testid="machines-two-sides-cta">
              Get Your Firm&rsquo;s Free Customised AI Roadmap
            </Pill>
            <Link to="/enterprise" data-testid="machines-two-sides-link" style={{ color: "var(--c-forest)", textDecoration: "none", borderBottom: "1px solid rgba(22,30,27,0.3)", paddingBottom: 2, fontSize: 14 }}>
              Visit For People &amp; Enterprises &#x2197;
            </Link>
          </div>
        </div>
        <div
          aria-hidden
          style={{
            background:
              "radial-gradient(120% 80% at 80% 20%, rgba(161,172,156,0.08) 0%, rgba(22,30,27,0) 60%), " +
              "linear-gradient(180deg, #17332B 0%, #0E1815 100%)",
            position: "relative",
            minHeight: 360,
            overflow: "hidden",
          }}
        >
          <svg viewBox="0 0 600 400" width="100%" height="100%" preserveAspectRatio="xMidYMid slice" style={{ position: "absolute", inset: 0 }}>
            <defs>
              <linearGradient id="beamA" x1="0" x2="1"><stop offset="0" stopColor="#A1AC9C" stopOpacity="0.9" /><stop offset="1" stopColor="#A1AC9C" stopOpacity="0" /></linearGradient>
              <linearGradient id="beamB" x1="1" x2="0"><stop offset="0" stopColor="#A1AC9C" stopOpacity="0.9" /><stop offset="1" stopColor="#A1AC9C" stopOpacity="0" /></linearGradient>
            </defs>
            {/* Central sovereign core */}
            <circle cx="300" cy="200" r="52" fill="none" stroke="#A1AC9C" strokeWidth="1.3" />
            <circle cx="300" cy="200" r="30" fill="rgba(161,172,156,0.12)" stroke="#A1AC9C" strokeWidth="1" />
            <text x="300" y="205" textAnchor="middle" fontFamily="'Berkeley Mono', ui-monospace, monospace" fontSize="10" fill="#A1AC9C" letterSpacing="1">CROWTHER</text>
            {/* Left cluster (People & Enterprises) */}
            {[[110, 130], [70, 200], [110, 270], [170, 100], [170, 300]].map(([x, y], i) => (
              <g key={"L" + i}>
                <line x1={x} y1={y} x2="252" y2="200" stroke="url(#beamA)" strokeWidth="1" strokeDasharray="2 4" />
                <rect x={x - 22} y={y - 12} width="44" height="24" rx="4" fill="rgba(161,172,156,0.06)" stroke="#A1AC9C" strokeOpacity="0.55" />
                <circle cx={x} cy={y} r="2.4" fill="#A1AC9C" />
              </g>
            ))}
            {/* Right cluster (Machines) */}
            {[[490, 130], [530, 200], [490, 270], [430, 100], [430, 300]].map(([x, y], i) => (
              <g key={"R" + i}>
                <line x1="348" y1="200" x2={x} y2={y} stroke="url(#beamB)" strokeWidth="1" strokeDasharray="2 4" />
                <polygon points={`${x - 12},${y - 10} ${x + 12},${y - 10} ${x + 14},${y + 10} ${x - 14},${y + 10}`} fill="rgba(161,172,156,0.06)" stroke="#A1AC9C" strokeOpacity="0.55" />
                <circle cx={x} cy={y} r="2.4" fill="#A1AC9C" />
              </g>
            ))}
            <text x="110" y="50" fontFamily="'Berkeley Mono', ui-monospace, monospace" fontSize="9" fill="#A1AC9C" opacity="0.75" letterSpacing="1.5">PEOPLE &amp; ENTERPRISES</text>
            <text x="490" y="50" textAnchor="end" fontFamily="'Berkeley Mono', ui-monospace, monospace" fontSize="9" fill="#A1AC9C" opacity="0.75" letterSpacing="1.5">MACHINES</text>
          </svg>
          <div style={{ position: "absolute", left: 24, bottom: 24, right: 24, fontFamily: "var(--font-sans)", fontSize: 11, letterSpacing: "0.22em", color: "var(--c-seafoam)", textTransform: "uppercase", opacity: 0.85 }}>
            {"\u25A0"} One stack &nbsp;&middot;&nbsp; Two audiences
          </div>
        </div>
      </section>

      {/* S11 · Build With Us --------------------------------------------- */}
      <section
        data-testid="machines-careers"
        style={{
          padding: "var(--section-py-desktop) var(--gutter-desktop)",
          background:
            "radial-gradient(80% 60% at 20% 20%, rgba(161,172,156,0.14) 0%, rgba(246,241,234,0) 60%), " +
            "linear-gradient(180deg, #ECE6DB 0%, #E4DCCE 100%)",
          borderTop: "1px solid rgba(22,30,27,0.12)",
        }}
      >
        <div style={{ maxWidth: "var(--container-max)", margin: "0 auto" }}>
          <Eyebrow glyph>BUILD WITH US</Eyebrow>
          <h2 style={{ fontFamily: "var(--font-sans)", fontSize: "clamp(32px, 3.6vw, 54px)", lineHeight: 1.06, letterSpacing: "-0.02em", marginTop: "var(--s-4)", maxWidth: "22ch" }}>
            Machines That Think Need Builders Who Care.
          </h2>
          <p style={{ color: "#3D4A44", fontSize: "17.5px", lineHeight: 1.6, maxWidth: "68ch", marginTop: "var(--s-4)" }}>
            We are building the team that brings intelligence to working machines: engineers who respect physics, love constraints and want their software to answer to the people who use it. If the idea of autonomy with the owner in command is the problem you want to work on, we want to hear from you.
          </p>
          <div style={{ marginTop: "var(--s-6)" }}>
            <Pill as={Link} to="/machines/contact" variant="primary" data-testid="machines-careers-cta">
              Talk to Crowther
            </Pill>
          </div>
        </div>
      </section>

      {/* S12 · Dormant proof sections — hidden behind SHOW_MACHINES_PROOF - */}
      {SHOW_MACHINES_PROOF && <MachinesProof />}

      {/* S13 · CTA band --------------------------------------------------- */}
      <section
        data-testid="machines-cta-band"
        style={{
          position: "relative",
          padding: "var(--statement-py-desktop) var(--gutter-desktop)",
          textAlign: "center",
          background: "var(--c-linen)",
          borderTop: "1px solid rgba(22,30,27,0.12)",
          borderBottom: "1px solid rgba(22,30,27,0.12)",
          overflow: "hidden",
          color: "var(--c-forest)",
        }}
      >
        {SHOW_MACHINES_ASSETS && (
          <>
            <div aria-hidden data-testid="machines-band-media" style={{ position: "absolute", inset: 0, backgroundImage: "url(/media/machines-band.jpg)", backgroundSize: "cover", backgroundPosition: "center bottom", pointerEvents: "none" }} />
            <div aria-hidden style={{ position: "absolute", inset: 0, background: "rgba(22,30,27,0.4)", pointerEvents: "none" }} />
          </>
        )}
        <p style={{ position: "relative", fontFamily: "var(--font-sans)", fontSize: "clamp(28px, 3.2vw, 44px)", lineHeight: 1.15, letterSpacing: "-0.02em", maxWidth: "26ch", margin: "0 auto var(--s-6)" }}>
          Learn more about Crowther for machines.
        </p>
        <div style={{ position: "relative", display: "flex", flexDirection: "column", alignItems: "center", gap: "var(--s-4)" }}>
          <Pill as={Link} to="/plan" variant="primary" data-testid="machines-band-cta">
            Get Your Firm&rsquo;s Free Customised AI Roadmap
          </Pill>
          <Link to="/machines/contact" data-testid="machines-band-secondary" style={{ color: "#3D4A44", textDecoration: "none", borderBottom: "1px solid rgba(22,30,27,0.12)", paddingBottom: 2, fontSize: 14 }}>
            Or talk to us directly &rarr;
          </Link>
        </div>
      </section>

      {/* S14 · Footer (dark) --------------------------------------------- */}
      <MachinesFooter />
    </div>
  );
}

/* ============================================================================
   Machines contact page (Part 7.3)
   ============================================================================ */

export function MachinesContact() {
  const [status, setStatus] = React.useState("idle");
  const [error, setError] = React.useState(null);

  async function onSubmit(e) {
    e.preventDefault();
    setStatus("submitting");
    setError(null);
    const fd = new FormData(e.currentTarget);
    const payload = {
      name: fd.get("name"),
      work_email: fd.get("email"),
      organisation: fd.get("organisation"),
      message: fd.get("message"),
      side: "machines",
    };
    try {
      const base = process.env.REACT_APP_BACKEND_URL || "";
      const r = await fetch(`${base}/api/marketing/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setStatus("ok");
    } catch (err) {
      setError(String(err && err.message ? err.message : err));
      setStatus("error");
    }
  }

  return (
    <div
      data-testid="machines-contact-root"
      style={{
        background: "var(--c-linen)",
        color: "var(--c-forest)",
        minHeight: "100vh",
        fontFamily: "var(--font-sans)",
      }}
    >
      <MachinesNav />
      <section
        style={{
          maxWidth: "820px",
          margin: "0 auto",
          padding: "var(--section-py-desktop) var(--gutter-desktop)",
        }}
      >
        <h1
          style={{
            fontFamily: "var(--font-sans)",
            fontSize: "var(--type-h1-size)",
            lineHeight: "var(--type-h1-lh)",
            letterSpacing: "-0.02em",
            margin: 0,
          }}
        >
          Talk to Crowther.
        </h1>
        <p
          style={{
            color: "#3D4A44",
            marginTop: "var(--s-3)",
            fontSize: "var(--type-body-size)",
          }}
        >
          Tell us about your machines and we will come back within two working days.
        </p>
        {status === "ok" ? (
          <div
            data-testid="machines-contact-confirmation"
            style={{
              marginTop: "var(--s-8)",
              padding: "var(--s-7)",
              border: "1px solid rgba(22,30,27,0.12)",
              borderRadius: "var(--radius-card)",
              display: "flex",
              alignItems: "center",
              gap: "var(--s-5)",
            }}
          >
            <span
              aria-hidden
              style={{
                width: 44, height: 44, borderRadius: "50%",
                background: "rgba(23,145,104,0.15)", color: "var(--c-emerald)",
                display: "inline-flex", alignItems: "center", justifyContent: "center",
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
                <path d="M5 12l4 4L19 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
            <div>
              <div style={{ fontFamily: "var(--font-sans)", fontSize: "22px" }}>Received.</div>
              <div style={{ color: "#3D4A44", marginTop: 4, fontSize: 14 }}>
                Thank you. Your message is with the team, and you will hear from us within two working days.
              </div>
            </div>
          </div>
        ) : (
          <form onSubmit={onSubmit} style={{ marginTop: "var(--s-7)", display: "grid", gap: "var(--s-4)" }}>
            <Field label="Full name">
              <input name="name" required data-testid="mc-input-name" style={inputStyle} />
            </Field>
            <Field label="Work email">
              <input name="email" type="email" required data-testid="mc-input-email" style={inputStyle} />
            </Field>
            <Field label="Organisation">
              <input name="organisation" required data-testid="mc-input-org" style={inputStyle} />
            </Field>
            <Field label="Message">
              <textarea name="message" rows={5} required data-testid="mc-input-message" style={{ ...inputStyle, resize: "vertical" }} />
            </Field>
            {error && (
              <div role="alert" style={{ color: "var(--c-seafoam)", fontSize: 13 }}>
                Something went wrong. Please try again. ({error})
              </div>
            )}
            <div>
              <Pill
                as="button"
                type="submit"
                variant="primary"
                disabled={status === "submitting"}
                data-testid="machines-contact-submit"
              >
                Talk to Crowther
              </Pill>
            </div>
          </form>
        )}
      </section>
      <MachinesFooter />
    </div>
  );
}

/* -------- helpers --------------------------------------------------------- */

const inputStyle = {
  background: "var(--c-surface-on-dark-4)",
  color: "var(--c-linen)",
  border: "1px solid rgba(22,30,27,0.12)",
  borderRadius: "var(--radius-chip)",
  padding: "12px 14px",
  fontFamily: "var(--font-sans)",
  fontSize: "15px",
  outline: "none",
  width: "100%",
};

function Field({ label, children }) {
  return (
    <label style={{ display: "block" }}>
      <div
        style={{
          fontFamily: "var(--font-sans)",
          fontSize: "12px",
          letterSpacing: "0.12em",
          textTransform: "uppercase",
          color: "#3D4A44",
          marginBottom: 6,
        }}
      >
        {label}
      </div>
      {children}
    </label>
  );
}

function ProductDiagram({ kind, tone = "dark" }) {
  const stroke = tone === "light" ? "#0F352A" : "#A1AC9C";
  const dim = tone === "light" ? "rgba(15,53,42,0.55)" : "rgba(217,237,230,0.55)";
  const wrap = (children) => (
    <svg viewBox="0 0 640 380" width="100%" height="100%" preserveAspectRatio="xMidYMid slice" aria-hidden style={{ position: "absolute", inset: 0 }}>
      <defs>
        <linearGradient id="pdFade" x1="0" x2="1">
          <stop offset="0" stopColor={stroke} stopOpacity="0" />
          <stop offset=".5" stopColor={stroke} stopOpacity=".9" />
          <stop offset="1" stopColor={stroke} stopOpacity="0" />
        </linearGradient>
      </defs>
      {children}
    </svg>
  );

  if (kind === "Machine Autonomy") {
    // A haul-truck-style silhouette on a plotted route
    return wrap(
      <>
        {/* Route */}
        <path d="M40 300 C 160 300, 200 210, 320 210 S 480 130, 600 130" fill="none" stroke="url(#pdFade)" strokeWidth="1.4" strokeDasharray="3 6" />
        {/* Waypoints */}
        {[[40, 300], [200, 258], [320, 210], [460, 158], [600, 130]].map(([x, y], i) => (
          <g key={i}>
            <circle cx={x} cy={y} r="3.2" fill={stroke} />
            <circle cx={x} cy={y} r="9" fill="none" stroke={stroke} strokeOpacity=".5" />
          </g>
        ))}
        {/* Truck silhouette on the route */}
        <g transform="translate(300, 180)">
          <rect x="0" y="10" width="90" height="26" rx="4" fill="rgba(161,172,156,0.14)" stroke={stroke} strokeWidth="1.2" />
          <rect x="6" y="0" width="34" height="14" rx="2" fill="rgba(161,172,156,0.14)" stroke={stroke} strokeWidth="1.2" />
          <circle cx="18" cy="40" r="6" fill="none" stroke={stroke} strokeWidth="1.2" />
          <circle cx="72" cy="40" r="6" fill="none" stroke={stroke} strokeWidth="1.2" />
          {/* Sensor cones */}
          <path d="M45 6 L 15 -22 L 75 -22 Z" fill="rgba(161,172,156,0.09)" stroke={stroke} strokeOpacity=".5" strokeDasharray="2 3" />
        </g>
        {/* HUD ticks */}
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <line key={i} x1={80 + i * 90} y1="340" x2={80 + i * 90} y2="352" stroke={dim} strokeWidth="0.8" />
        ))}
        <text x="40" y="46" fontFamily="'Berkeley Mono', monospace" fontSize="10" fill={stroke} opacity=".85">MODE · SUPERVISED</text>
        <text x="600" y="46" textAnchor="end" fontFamily="'Berkeley Mono', monospace" fontSize="10" fill={stroke} opacity=".85">CONF 0.94</text>
      </>
    );
  }

  if (kind === "Machine OS") {
    // Fleet nodes coordinating through a central OS core
    return wrap(
      <>
        <circle cx="320" cy="190" r="120" fill="none" stroke={stroke} strokeOpacity=".3" strokeDasharray="1 6" />
        <circle cx="320" cy="190" r="80" fill="none" stroke={stroke} strokeOpacity=".45" strokeDasharray="1 4" />
        <rect x="286" y="164" width="68" height="52" rx="6" fill="rgba(161,172,156,0.10)" stroke={stroke} strokeWidth="1.2" />
        <text x="320" y="195" textAnchor="middle" fontFamily="'Berkeley Mono', monospace" fontSize="10" fill={stroke} letterSpacing="1">OS</text>
        {[
          [140, 90, "HAULER"], [500, 90, "LOADER"], [560, 260, "YARD"], [80, 260, "CRANE"], [180, 320, "AGV-3"], [460, 320, "AGV-1"],
        ].map(([x, y, label], i) => (
          <g key={i}>
            <line x1={x} y1={y} x2="320" y2="190" stroke={stroke} strokeOpacity=".55" strokeWidth="1" />
            <rect x={x - 30} y={y - 12} width="60" height="24" rx="3" fill="rgba(161,172,156,0.10)" stroke={stroke} strokeOpacity=".7" />
            <text x={x} y={y + 4} textAnchor="middle" fontFamily="'Berkeley Mono', monospace" fontSize="9" fill={stroke}>{label}</text>
          </g>
        ))}
        <text x="40" y="46" fontFamily="'Berkeley Mono', monospace" fontSize="10" fill={stroke} opacity=".85">FLEET · 34 UNITS</text>
        <text x="600" y="46" textAnchor="end" fontFamily="'Berkeley Mono', monospace" fontSize="10" fill={stroke} opacity=".85">OTA · v2.14.3</text>
      </>
    );
  }

  if (kind === "Machine Data Engine") {
    // A learning curve + bar chart
    return wrap(
      <>
        <g stroke={stroke} strokeOpacity=".2" strokeWidth="0.8">
          {[80, 130, 180, 230, 280].map((y) => (<line key={y} x1="60" y1={y} x2="600" y2={y} />))}
        </g>
        <path d="M60 260 C 160 250, 240 210, 320 180 S 480 110, 600 90" fill="none" stroke={stroke} strokeWidth="1.6" />
        <path d="M60 260 C 160 250, 240 210, 320 180 S 480 110, 600 90 L 600 320 L 60 320 Z" fill="url(#pdFade)" opacity=".18" />
        {/* Sample points */}
        {[[100, 254], [200, 224], [300, 192], [400, 152], [500, 116], [600, 90]].map(([x, y], i) => (
          <g key={i}>
            <circle cx={x} cy={y} r="3" fill={stroke} />
            <line x1={x} y1={y + 6} x2={x} y2="320" stroke={dim} strokeOpacity=".25" strokeDasharray="1 3" />
          </g>
        ))}
        {/* Bar chart at bottom */}
        {[42, 60, 74, 88, 95, 110, 120, 132].map((h, i) => (
          <rect key={i} x={80 + i * 62} y={320 - h} width="26" height={h} fill="rgba(161,172,156,0.30)" stroke={stroke} strokeWidth="0.8" />
        ))}
        <text x="40" y="46" fontFamily="'Berkeley Mono', monospace" fontSize="10" fill={stroke} opacity=".85">LOSS 0.032 · EPOCHS 128</text>
        <text x="600" y="46" textAnchor="end" fontFamily="'Berkeley Mono', monospace" fontSize="10" fill={stroke} opacity=".85">DATA 42.1M SAMPLES</text>
      </>
    );
  }

  // Operator Intelligence — control-room dashboard grid
  return wrap(
    <>
      {/* Video tile grid */}
      {[0, 1, 2, 3].map((c) =>
        [0, 1].map((r) => (
          <g key={c + "-" + r}>
            <rect x={40 + c * 140} y={70 + r * 130} width="120" height="110" rx="6" fill="rgba(161,172,156,0.08)" stroke={stroke} strokeOpacity=".55" />
            <circle cx={40 + c * 140 + 12} cy={70 + r * 130 + 12} r="3" fill={r === 0 && c === 1 ? "#FFBC47" : "#28C840"} />
            <text x={40 + c * 140 + 22} y={70 + r * 130 + 15} fontFamily="'Berkeley Mono', monospace" fontSize="8" fill={stroke} opacity=".85">
              CAM {c + 1}-{r + 1}
            </text>
            {/* scan lines */}
            <line x1={40 + c * 140} y1={70 + r * 130 + 50} x2={40 + c * 140 + 120} y2={70 + r * 130 + 50} stroke={stroke} strokeOpacity=".25" />
            <line x1={40 + c * 140} y1={70 + r * 130 + 80} x2={40 + c * 140 + 120} y2={70 + r * 130 + 80} stroke={stroke} strokeOpacity=".15" />
          </g>
        ))
      )}
      {/* Bottom command line */}
      <rect x="40" y="330" width="560" height="24" fill="rgba(161,172,156,0.10)" stroke={stroke} strokeOpacity=".4" />
      <text x="50" y="346" fontFamily="'Berkeley Mono', monospace" fontSize="10" fill={stroke}>op&gt; HOLD haul-07 &nbsp; ACK</text>
      <text x="40" y="46" fontFamily="'Berkeley Mono', monospace" fontSize="10" fill={stroke} opacity=".85">OPERATOR · CONTROL ROOM</text>
      <text x="600" y="46" textAnchor="end" fontFamily="'Berkeley Mono', monospace" fontSize="10" fill={stroke} opacity=".85">8 MACHINES</text>
    </>
  );
}

function MachinesHeroVideo() {
  const ref = React.useRef(null);
  const [muted, setMuted] = React.useState(true);

  const toggle = React.useCallback(() => {
    const v = ref.current;
    if (!v) return;
    const next = !v.muted;
    v.muted = next;
    if (!next) {
      const p = v.play();
      if (p && typeof p.then === "function") p.catch(() => {});
    }
    setMuted(next);
  }, []);

  return (
    <div
      data-testid="machines-hero-video"
      style={{
        position: "relative",
        aspectRatio: "16 / 10",
        borderRadius: "var(--radius-media)",
        overflow: "hidden",
        background: "var(--c-navy)",
        border: "1px solid rgba(22,30,27,0.12)",
        boxShadow: "0 20px 60px rgba(0,0,0,0.45)",
      }}
    >
      <video
        ref={ref}
        src="/media/crowther-corporate.mp4"
        poster="/media/machines-hero-poster.jpg"
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        aria-label="Crowther for machines film"
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
          display: "block",
        }}
      />
      <button
        type="button"
        onClick={toggle}
        data-testid="machines-hero-video-sound-toggle"
        aria-pressed={!muted}
        aria-label={muted ? "Play sound" : "Mute sound"}
        style={{
          position: "absolute",
          right: 14,
          bottom: 14,
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          padding: "8px 14px",
          borderRadius: 999,
          background: "rgba(22,30,27,0.72)",
          backdropFilter: "blur(10px)",
          WebkitBackdropFilter: "blur(10px)",
          border: "1px solid rgba(246,241,234,0.2)",
          color: "var(--c-linen)",
          fontFamily: "var(--font-sans)",
          fontSize: 12,
          letterSpacing: "0.04em",
          cursor: "pointer",
        }}
      >
        {muted ? (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
            <path d="M11 5L6 9H3v6h3l5 4V5z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" strokeLinecap="round" fill="none"/>
            <path d="M17 9l4 6M21 9l-4 6" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"/>
          </svg>
        ) : (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
            <path d="M11 5L6 9H3v6h3l5 4V5z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" strokeLinecap="round" fill="none"/>
            <path d="M16 8.5a5 5 0 010 7M19 6a8 8 0 010 12" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"/>
          </svg>
        )}
        <span>{muted ? "Play sound" : "Mute"}</span>
      </button>
    </div>
  );
}

export function MachinesNav() {
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);
  return (
    <nav
      data-testid="machines-nav" style={{
        position: "sticky",
        top: 0,
        zIndex: "var(--z-nav)",
        height: 64,
        background: "rgba(246,241,234,0.72)",
        backdropFilter: "blur(20px) saturate(180%)",
        WebkitBackdropFilter: "blur(20px) saturate(180%)",
        borderBottom: "1px solid rgba(22,30,27,0.10)",
        boxShadow: "inset 0 1px 0 0 rgba(255,255,255,0.5)",
        display: "flex",
        alignItems: "center",
        padding: "0 var(--gutter-desktop)",
      }}
    >
      <Link to="/?choose=1" data-testid="machines-nav-brand" aria-label="Crowther — Home" style={{ display: "inline-flex" }}>
        <WordmarkChip tone="light" size={22} />
      </Link>
      <div style={{ display: "flex", alignItems: "center", gap: "28px", marginLeft: "auto", flexWrap: "nowrap", whiteSpace: "nowrap" }}>
        <Link to="/machines" style={navLinkStyle} data-testid="machines-nav-home">Home</Link>
        <Link to="/machines/products" style={navLinkStyle} data-testid="machines-nav-products">Products</Link>
        <Link to="/machines/industries" style={navLinkStyle} data-testid="machines-nav-industries">Industries</Link>
        <Link to="/machines/approach" style={navLinkStyle} data-testid="machines-nav-approach">Approach</Link>
        <Link to="/machines/contact" style={navLinkStyle} data-testid="machines-nav-contact">Contact</Link>
        <AudienceSwitch to="/enterprise" label="For People & Enterprises" tone="light" />
        <Pill as={Link} to="/plan" variant="primary" data-testid="machines-nav-cta">
          Get Your Firm&rsquo;s Free Customised AI Roadmap
        </Pill>
        <button
          type="button"
          className="machines-nav-burger"
          aria-label="Menu"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
          data-testid="machines-nav-menu"
        >
          <svg width="26" height="18" viewBox="0 0 26 18" fill="none" aria-hidden>
            <path d="M0 1h26M0 9h26M0 17h26" stroke="#161E1B" strokeWidth="1.5" />
          </svg>
        </button>
      </div>

      {/* Mobile drawer */}
      <div
        className={`machines-nav-drawer${open ? " is-open" : ""}`}
        data-testid="machines-nav-drawer"
        role="dialog"
        aria-hidden={!open}
      >
        <Link to="/machines" style={drawerLinkStyle} onClick={close} data-testid="machines-nav-drawer-home">Home</Link>
        <Link to="/machines/products" style={drawerLinkStyle} onClick={close} data-testid="machines-nav-drawer-products">Products</Link>
        <Link to="/machines/industries" style={drawerLinkStyle} onClick={close} data-testid="machines-nav-drawer-industries">Industries</Link>
        <Link to="/machines/approach" style={drawerLinkStyle} onClick={close} data-testid="machines-nav-drawer-approach">Approach</Link>
        <Link to="/machines/contact" style={drawerLinkStyle} onClick={close} data-testid="machines-nav-drawer-contact">Contact</Link>
        <Link to="/enterprise" style={{ ...drawerLinkStyle, opacity: 0.65 }} onClick={close} data-testid="machines-nav-drawer-enterprise">For People &amp; Enterprises &rarr;</Link>
        <Link to="/plan" className="machines-nav-drawer-cta" onClick={close} data-testid="machines-nav-drawer-cta">Get Your Firm&rsquo;s Free Customised AI Roadmap &rarr;</Link>
      </div>
    </nav>
  );
}

const drawerLinkStyle = {
  fontFamily: "var(--font-sans)",
  fontSize: "18px",
  color: "var(--c-forest)",
  textDecoration: "none",
  padding: "10px 0",
  borderBottom: "1px solid rgba(22,30,27,0.08)",
};

const navLinkStyle = {
  fontFamily: "var(--font-sans)",
  fontSize: "14px",
  color: "var(--c-forest)",
  textDecoration: "none",
  opacity: 0.85,
};

export function MachinesFooter() {
  return (
    <footer
      data-testid="machines-footer"
      style={{
        background: "var(--c-forest)",
        color: "#3D4A44",
        padding: "var(--s-9) var(--gutter-desktop) var(--s-6)",
        borderTop: "1px solid rgba(22,30,27,0.12)",
        fontSize: 13,
      }}
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1.6fr 2fr",
          gap: "var(--s-8)",
          maxWidth: "var(--container-max)",
          margin: "0 auto",
          alignItems: "flex-start",
        }}
      >
        <div
          style={{
            fontFamily: "var(--font-sans)",
            fontSize: "clamp(22px, 2vw, 30px)",
            lineHeight: 1.28,
            letterSpacing: "-0.014em",
            maxWidth: "34ch",
            color: "var(--c-linen)",
          }}
        >
          Our mission is to bring intelligence to a billion machines, under the control of the people who own them.
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: "var(--s-6)",
          }}
        >
          <FooterCol title="For Machines">
            <Link to="/machines" style={footerLinkStyle}>Overview</Link>
            <Link to="/machines/products" style={footerLinkStyle}>Products</Link>
            <Link to="/machines/industries" style={footerLinkStyle}>Industries</Link>
            <Link to="/machines/approach" style={footerLinkStyle}>Approach</Link>
            <Link to="/machines/contact" style={footerLinkStyle}>Talk to Crowther</Link>
          </FooterCol>
          <FooterCol title="For People &amp; Enterprises">
            <Link to="/enterprise" style={footerLinkStyle}>Overview</Link>
            <Link to="/enterprise/solution" style={footerLinkStyle}>What We Build</Link>
            <Link to="/enterprise/research" style={footerLinkStyle}>Research</Link>
            <Link to="/enterprise/contact" style={footerLinkStyle}>Contact Us</Link>
          </FooterCol>
          <FooterCol title="Company">
            <Link to="/terms" style={footerLinkStyle}>Terms of Service</Link>
            <Link to="/privacy" style={footerLinkStyle}>Privacy Policy</Link>
          </FooterCol>
        </div>
      </div>
      <div
        style={{
          maxWidth: "var(--container-max)",
          margin: "var(--s-8) auto 0",
          paddingTop: "var(--s-5)",
          borderTop: "1px solid rgba(22,30,27,0.12)",
          display: "flex",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: "var(--s-3)",
          color: "#3D4A44",
          fontSize: 12.5,
        }}
      >
        <span>&copy; 2026 Crowther. All rights reserved.</span>
        <span>Intelligence for the machines that move the world.</span>
      </div>
    </footer>
  );
}

const footerLinkStyle = {
  color: "#3D4A44",
  textDecoration: "none",
  fontSize: 14,
};

function FooterCol({ title, children }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div
        style={{
          fontFamily: "var(--font-sans)",
          fontSize: 11,
          letterSpacing: "0.16em",
          textTransform: "uppercase",
          color: "var(--c-linen)",
          opacity: 0.7,
          marginBottom: 4,
        }}
        dangerouslySetInnerHTML={{ __html: title }}
      />
      {children}
    </div>
  );
}

/* -------- Dormant proof sections (Part 9, gated by SHOW_MACHINES_PROOF) -- */

function MachinesProof() {
  const [news, setNews] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const base = process.env.REACT_APP_BACKEND_URL || "";
    fetch(`${base}/api/marketing/machine-news?limit=6`)
      .then((r) => (r.ok ? r.json() : { items: [] }))
      .then((d) => setNews(Array.isArray(d.items) ? d.items : []))
      .catch(() => setNews([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <>
      {/* Partner strip ------------------------------------------------- */}
      <section
        data-testid="machines-partners"
        style={{
          padding: "var(--section-py-desktop) var(--gutter-desktop)",
          borderTop: "1px solid rgba(22,30,27,0.12)",
          maxWidth: "var(--container-max)",
          margin: "0 auto",
        }}
      >
        <Eyebrow glyph>PARTNERS</Eyebrow>
        <div
          style={{
            marginTop: "var(--s-6)",
            display: "grid",
            gridTemplateColumns: "repeat(6, 1fr)",
            gap: "var(--s-6)",
            alignItems: "center",
          }}
        >
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              data-testid={`machines-partner-${i + 1}`}
              aria-hidden
              style={{
                height: 32,
                background: "var(--c-surface-on-dark-4)",
                border: "1px solid rgba(22,30,27,0.12)",
                borderRadius: 4,
                opacity: 0.7,
              }}
            />
          ))}
        </div>
      </section>

      {/* Testimonial --------------------------------------------------- */}
      <section
        data-testid="machines-testimonial"
        style={{
          padding: "var(--section-py-desktop) var(--gutter-desktop)",
          borderTop: "1px solid rgba(22,30,27,0.12)",
          maxWidth: "var(--container-max)",
          margin: "0 auto",
        }}
      >
        <Eyebrow glyph>IN THE FIELD</Eyebrow>
        <blockquote
          style={{
            fontFamily: "var(--font-sans)",
            fontSize: "var(--type-h2-size)",
            lineHeight: "var(--type-h2-lh)",
            letterSpacing: "-0.02em",
            maxWidth: "28ch",
            margin: "var(--s-5) 0 var(--s-6)",
          }}
        >
          Placeholder testimonial. Awaiting a signed quote from an owner-operator.
        </blockquote>
        <div
          style={{
            fontFamily: "var(--font-sans)",
            fontSize: 13,
            letterSpacing: "0.02em",
            color: "#3D4A44",
          }}
        >
          [ATTRIBUTION LINE], [ROLE], [OPERATOR]
        </div>
      </section>

      {/* News grid ----------------------------------------------------- */}
      <section
        data-testid="machines-news"
        style={{
          padding: "var(--section-py-desktop) var(--gutter-desktop)",
          borderTop: "1px solid rgba(22,30,27,0.12)",
          maxWidth: "var(--container-max)",
          margin: "0 auto",
        }}
      >
        <Eyebrow glyph>NEWS</Eyebrow>
        <h2
          style={{
            fontFamily: "var(--font-sans)",
            fontSize: "var(--type-h2-size)",
            lineHeight: "var(--type-h2-lh)",
            letterSpacing: "-0.02em",
            marginTop: "var(--s-4)",
            marginBottom: "var(--s-6)",
            maxWidth: "22ch",
          }}
        >
          Signals from the programme.
        </h2>
        {loading ? (
          <p style={{ color: "#3D4A44" }}>Loading.</p>
        ) : news.length === 0 ? (
          <p
            data-testid="machines-news-empty"
            style={{ color: "#3D4A44", maxWidth: "50ch" }}
          >
            No published items yet. Entries appear here once the machine_news
            collection contains published rows.
          </p>
        ) : (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: "var(--s-5)",
            }}
          >
            {news.slice(0, 3).map((n, i) => (
              <a
                key={n.id || i}
                href={n.url || "#"}
                data-testid={`machines-news-card-${i + 1}`}
                style={{
                  display: "block",
                  color: "var(--c-linen)",
                  textDecoration: "none",
                  background: "var(--c-surface-on-dark-4)",
                  border: "1px solid rgba(22,30,27,0.12)",
                  borderRadius: "var(--radius-card)",
                  padding: "var(--s-5)",
                }}
              >
                <div
                  aria-hidden
                  style={{
                    aspectRatio: "16 / 9",
                    background: "var(--c-navy)",
                    borderRadius: "var(--radius-media)",
                    marginBottom: "var(--s-4)",
                    backgroundImage: n.image_url ? `url(${n.image_url})` : undefined,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                  }}
                />
                <div
                  style={{
                    fontFamily: "var(--font-sans)",
                    fontSize: 11,
                    letterSpacing: "0.16em",
                    textTransform: "uppercase",
                    color: "#3D4A44",
                    marginBottom: 6,
                  }}
                >
                  {n.tag || "News"} · {n.date || ""}
                </div>
                <div
                  style={{
                    fontFamily: "var(--font-sans)",
                    fontSize: "var(--type-h3-size)",
                    lineHeight: "var(--type-h3-lh)",
                    letterSpacing: "-0.015em",
                  }}
                >
                  {n.title}
                </div>
              </a>
            ))}
          </div>
        )}
      </section>
    </>
  );
}

/* -------- Icons moved to /components/shared/Icons.jsx -------------------- */
